// A new price list version for a supplier (M6): the day it takes effect and, per item, the
// list price and the discount entered separately (plan §5.4). The net price follows; a
// discount that moves against the item's current one is flagged before saving, as the
// server will flag it, with which way the net price moves and by how much (UI6A-7). Items
// left blank keep no price in this version.

import type { SupplierDetail, SupplierItem } from '@enp/domain/api';
import {
  Badge,
  Button,
  cn,
  DateField,
  EmptyState,
  FormField,
  Input,
  Money,
  PageHeader,
  Skeleton,
  Textarea,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Save, TriangleAlert } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { newId } from '@enp/domain/ids';
import { meQueryOptions } from '../../../../../../auth/session.js';
import {
  KeyHints,
  NoPermission,
  PurchasingBreadcrumb,
  useCan,
} from '../../../../../../purchasing/parts.js';
import {
  createPriceList,
  invalidatePurchasing,
  supplierQuery,
} from '../../../../../../purchasing/queries.js';
import { priceMove } from '../../../../../../purchasing/figures.js';
import {
  discountInput,
  discountText,
  netOf,
  packText,
  parseDiscount,
} from '../../../../../../purchasing/words.js';
import { useDocumentTitle } from '../../../../../../shell/page.js';
import { ErrorPanel } from '../../../../../../shell/route-states.js';
import { Notice } from '../../../../../../stations/page-parts.js';
import { accountToday } from '../../../../../../stock/labels.js';
import { actionProblem } from '../../../../../../stock-control/parts.js';
import { parseRand, randInput } from '../../../../../../stock-control/words.js';

export const Route = createFileRoute('/_app/purchasing/suppliers/$supplierId/price-lists/new')({
  component: NewPriceListPage,
});

interface Row {
  list: string;
  discount: string;
}

function rowsOf(items: readonly SupplierItem[]): Record<string, Row> {
  return Object.fromEntries(
    items.map((i) => [
      i.id,
      {
        list: i.current_price ? randInput(i.current_price.list_price_cents) : '',
        discount: i.current_price ? discountInput(i.current_price.discount_bp) : '',
      },
    ]),
  );
}

function PriceRows({
  supplier,
  rows,
  setRow,
  errors,
}: {
  supplier: SupplierDetail;
  rows: Record<string, Row>;
  setRow: (id: string, patch: Partial<Row>) => void;
  errors: Record<string, string>;
}) {
  const live = supplier.items.filter((i) => i.archived_at === null);
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table aria-label="Prices" className="w-full border-collapse text-body">
        <thead>
          <tr className="bg-surface-sunken text-left text-xs font-semibold uppercase tracking-wide text-muted">
            <th scope="col" className="px-3 py-2">
              Item
            </th>
            <th scope="col" className="w-40 px-3 py-2">
              List price per pack (R)
            </th>
            <th scope="col" className="w-32 px-3 py-2">
              Discount (%)
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              Net per pack
            </th>
            <th scope="col" className="px-3 py-2">
              Against today’s price
            </th>
          </tr>
        </thead>
        <tbody>
          {live.map((i) => {
            const r = rows[i.id] ?? { list: '', discount: '' };
            const list = r.list.trim() === '' ? null : parseRand(r.list);
            const bp = parseDiscount(r.discount);
            const net = list !== null && bp !== null ? netOf(list, bp) : null;
            const cur = i.current_price;
            const discountMoved =
              cur !== null && list !== null && bp !== null && bp !== cur.discount_bp;
            const move = cur !== null && net !== null ? priceMove(cur.net_price_cents, net) : null;
            return (
              <tr key={i.id} className="border-t border-border align-top">
                <td className="px-3 py-2">
                  <span className="flex flex-col leading-tight">
                    <span className="font-medium text-fg">{i.product.name}</span>
                    <span className="text-xs text-muted">
                      {packText(i, i.product.base_uom)}
                      {i.supplier_code && (
                        <>
                          {' '}
                          · their code <span className="whitespace-nowrap">{i.supplier_code}</span>
                        </>
                      )}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2">
                  <Input
                    inputMode="decimal"
                    aria-label={`List price of ${i.product.name} (R per ${i.pack_uom})`}
                    aria-invalid={errors[i.id] ? true : undefined}
                    value={r.list}
                    placeholder="Not priced"
                    onChange={(e) => setRow(i.id, { list: e.target.value })}
                    className="text-right tabular-nums"
                  />
                  {errors[i.id] && (
                    <p role="alert" className="mt-1 text-xs font-medium text-state-danger">
                      {errors[i.id]}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Input
                    inputMode="decimal"
                    aria-label={`Discount on ${i.product.name} (%)`}
                    value={r.discount}
                    placeholder="0"
                    onChange={(e) => setRow(i.id, { discount: e.target.value })}
                    className="text-right tabular-nums"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  {net === null ? (
                    <span className="text-sm text-muted">—</span>
                  ) : (
                    <Money cents={net} className="font-medium text-fg" />
                  )}
                </td>
                <td className="px-3 py-2 text-sm">
                  {r.list.trim() !== '' && list === null ? (
                    // Typed but not a price: it isn't "left out" on purpose (UI6A-111).
                    <span className="font-medium text-state-warning">
                      Fix the price to include it
                    </span>
                  ) : cur === null ? (
                    <span className="text-muted">{list === null ? 'No price' : 'New price'}</span>
                  ) : list === null ? (
                    <span className="text-muted">
                      Today <Money cents={cur.net_price_cents} />; this version leaves it out
                    </span>
                  ) : (
                    <span className="flex flex-col gap-1" data-testid="against-today">
                      <span className="text-muted">
                        Today <Money cents={cur.net_price_cents} />
                        {cur.discount_bp > 0 && ` (${discountText(cur.discount_bp)} off)`}
                      </span>
                      {move && move.direction !== 'same' && (
                        <span
                          className={cn(
                            'whitespace-nowrap font-medium',
                            move.direction === 'up' ? 'text-state-warning' : 'text-state-success',
                          )}
                          data-testid="price-move"
                        >
                          {move.direction === 'up' ? 'Up ' : 'Down '}
                          {move.text}
                        </span>
                      )}
                      {discountMoved && bp !== null && (
                        <Badge tone={move?.direction === 'up' ? 'warning' : 'neutral'}>
                          {move?.direction === 'up' && <TriangleAlert aria-hidden />}
                          Discount {discountText(cur.discount_bp).toLowerCase()} →{' '}
                          {discountText(bp).toLowerCase()}
                        </Badge>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NewPriceListPage() {
  const { supplierId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const canEdit = useCan('purchasing.order', true);
  const q = useQuery(supplierQuery(supplierId));
  const [effective, setEffective] = useState<string | undefined>(today);
  const [note, setNote] = useState('');
  const [rows, setRows] = useState<Record<string, Row> | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  useDocumentTitle(q.data ? `New price list · ${q.data.name}` : 'New price list');
  const current =
    rows ?? (q.data ? rowsOf(q.data.items.filter((i) => i.archived_at === null)) : {});

  // One key per form: a retried save answers the first version (D-063).
  const attempt = useRef(newId());
  const create = useMutation({
    mutationFn: (body: Parameters<typeof createPriceList>[1]) =>
      createPriceList(supplierId, body, attempt.current),
    onSuccess: async (pl) => {
      await invalidatePurchasing(queryClient);
      toast.success(`Version ${pl.version_no} saved`, {
        description:
          pl.discount_changes > 0
            ? `${pl.discount_changes} discount ${pl.discount_changes === 1 ? 'change is' : 'changes are'} flagged on it.`
            : 'No discount changed.',
      });
      void navigate({ to: '/purchasing/price-lists/$priceListId', params: { priceListId: pl.id } });
    },
  });
  const failed = create.isError ? actionProblem(create.error, 'The price list wasn’t saved') : null;

  const setRow = (id: string, patch: Partial<Row>) => {
    setRows({ ...current, [id]: { ...(current[id] ?? { list: '', discount: '' }), ...patch } });
    if (errors[id]) setErrors((e) => ({ ...e, [id]: '' }));
    setFormError(undefined);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!effective) {
      setFormError('Choose the day it takes effect.');
      return;
    }
    const next: Record<string, string> = {};
    const prices: { supplier_item_id: string; list_price_cents: number; discount_bp: number }[] =
      [];
    for (const [id, r] of Object.entries(current)) {
      if (r.list.trim() === '') {
        if (r.discount.trim() !== '' && r.discount.trim() !== '0') {
          next[id] = 'Give the list price too, or clear the discount.';
        }
        continue;
      }
      const list = parseRand(r.list);
      const bp = parseDiscount(r.discount);
      if (list === null) next[id] = 'A rand amount, like 125.50.';
      else if (bp === null) next[id] = 'The discount is 0 to 100%, like 12.5.';
      else prices.push({ supplier_item_id: id, list_price_cents: list, discount_bp: bp });
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (prices.length === 0) {
      setFormError('Price at least one item.');
      return;
    }
    create.mutate({
      effective_from: effective,
      prices,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  const s = q.data;
  return (
    <>
      <PageHeader
        eyebrow={
          <PurchasingBreadcrumb
            trail={[
              { to: '/purchasing/suppliers', label: 'Suppliers' },
              ...(s
                ? [
                    {
                      to: '/purchasing/suppliers/$supplierId' as const,
                      params: { supplierId },
                      label: s.name,
                    },
                  ]
                : []),
            ]}
            page="New price list"
          />
        }
        title={s ? `New price list for ${s.name}` : 'New price list'}
        description="List price and discount are entered separately; the net price is worked out. Today’s prices are filled in: change what changed. Prices are per pack, ex VAT."
      />
      {canEdit === false ? (
        <NoPermission>
          Prices are entered by people who order stock for every branch. Ask the owner.
        </NoPermission>
      ) : q.isError ? (
        <ErrorPanel
          error={q.error}
          titleAs="h2"
          what="The supplier"
          onRetry={() => void q.refetch()}
        />
      ) : !s ? (
        <Skeleton className="h-64 w-full" />
      ) : s.items.every((i) => i.archived_at !== null) ? (
        <EmptyState
          icon={TriangleAlert}
          title="Nothing to price"
          description={`Add what you buy from ${s.name} first.`}
          action={
            <Button asChild>
              <Link to="/purchasing/suppliers/$supplierId" params={{ supplierId }}>
                Open {s.name}
              </Link>
            </Button>
          }
        />
      ) : (
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-5"
          // Ctrl/⌘+Enter saves from any field (UI6A-110).
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              e.currentTarget.requestSubmit();
            }
          }}
        >
          <div className="grid gap-4 sm:grid-cols-[14rem_1fr]">
            <FormField
              label="Takes effect on"
              required
              description={
                effective && effective < today
                  ? 'In the past: orders already made keep their prices; new ones use these from now.'
                  : 'Orders from this day use these prices.'
              }
            >
              {(control) => (
                <DateField {...control} value={effective} onChange={setEffective} today={today} />
              )}
            </FormField>
            <FormField
              label="Note (optional)"
              description="For example, “Price list emailed 28 Sep”."
            >
              <Textarea
                rows={1}
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
              />
            </FormField>
          </div>
          <PriceRows supplier={s} rows={current} setRow={setRow} errors={errors} />
          {formError && (
            <p role="alert" className="text-body font-medium text-state-danger">
              {formError}
            </p>
          )}
          {failed && (
            <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
              {failed.explain}
            </Notice>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="mr-auto">
              <KeyHints keys={[['Ctrl+Enter', 'save']]} />
            </span>
            <Button type="button" variant="secondary" asChild>
              <Link to="/purchasing/suppliers/$supplierId" params={{ supplierId }}>
                Cancel
              </Link>
            </Button>
            <Button
              type="submit"
              icon={<Save aria-hidden />}
              loading={create.isPending}
              aria-keyshortcuts="Control+Enter Meta+Enter"
            >
              Save price list
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
